import React from "react";
import { FiClock, FiRepeat, FiAward, FiAlertTriangle, FiShield } from "react-icons/fi";
import { useBuilder } from "./BuilderContext";
import { treeHasHistorical } from "./ruleModel";
import { Card, Field, TextInput, Chip } from "./ui";

const errText = (errors, path) => errors.find((e) => e.path === path)?.message;

const MODE_ICONS = {
  one_time: FiClock,
  repeating: FiRepeat,
  milestone: FiAward,
};

const DESC = {
  one_time: "Fires once per user account, ever.",
  repeating: "Fires every time conditions match (with optional cooldown window).",
  milestone: "Fires at repeating aggregate intervals (requires a history check).",
};

export default function StepRewardMode({ draft, errors, onChange }) {
  const { meta } = useBuilder();
  const modes = meta?.reward_modes || [];
  const hasHistory = treeHasHistorical(draft.condition);
  const num = (v) => (v === "" ? null : Number(v));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-bold text-gray-900">Define Reward Frequency & Cooldown</h3>
        <p className="text-xs text-gray-500">Configure how often users qualify for this rule and establish safety caps.</p>
      </div>

      {/* Reward Modes Selection Grid */}
      <div className="grid gap-4 md:grid-cols-3">
        {modes.map((m) => {
          const IconComponent = MODE_ICONS[m.name] || FiAward;
          const active = draft.reward_mode === m.name;
          return (
            <button key={m.name} type="button" onClick={() => onChange({ reward_mode: m.name })} className="text-left">
              <Card active={active} className="p-4 h-full cursor-pointer">
                <div className="flex items-center justify-between mb-2">
                  <div className={`p-2 rounded-xl transition-colors ${active ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}>
                    <IconComponent className="h-4 w-4" />
                  </div>
                  {active && <Chip color="blue font-bold">Selected</Chip>}
                </div>
                <span className="font-bold text-sm text-gray-900 block mb-1">{m.label}</span>
                <p className="text-xs text-gray-500 leading-relaxed">{DESC[m.name]}</p>
              </Card>
            </button>
          );
        })}
      </div>

      {/* Mode Specific Parameters */}
      <div className="bg-gray-50/60 rounded-2xl border border-gray-200/80 p-5 space-y-4">
        {draft.reward_mode === "milestone" && (
          <div className="space-y-3">
            {!hasHistory && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3.5 text-xs text-amber-800 flex items-start gap-2.5 shadow-2xs">
                <FiAlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold block text-amber-900">History Check Required</span>
                  <span>Milestone mode requires a history check in Step 2 (Conditions) to track the running metric count.</span>
                </div>
              </div>
            )}
            <Field label="Milestone Interval" error={errText(errors, "milestone_interval")} hint="Frequency threshold (e.g. every 50 scans)" className="max-w-md">
              <TextInput
                type="number"
                placeholder="e.g. 50"
                value={draft.milestone_interval ?? ""}
                onChange={(e) => onChange({ milestone_interval: num(e.target.value) })}
                error={errText(errors, "milestone_interval")}
              />
            </Field>
          </div>
        )}

        {draft.reward_mode === "repeating" && (
          <Field label="Cooldown Period (Hours, Optional)" error={errText(errors, "cooldown_hours")} hint="Minimum mandatory gap between consecutive firings" className="max-w-md">
            <TextInput
              type="number"
              placeholder="e.g. 24"
              value={draft.cooldown_hours ?? ""}
              onChange={(e) => onChange({ cooldown_hours: num(e.target.value) })}
              error={errText(errors, "cooldown_hours")}
            />
          </Field>
        )}

        <Field label="Maximum Triggers Per User (Optional)" error={errText(errors, "max_triggers_per_user")} hint="Hard lifetime limit across all executions per user" className="max-w-md">
          <TextInput
            type="number"
            placeholder="e.g. 10 (Leave blank for unlimited)"
            value={draft.max_triggers_per_user ?? ""}
            onChange={(e) => onChange({ max_triggers_per_user: num(e.target.value) })}
            error={errText(errors, "max_triggers_per_user")}
          />
        </Field>
      </div>
    </div>
  );
}

